import { WSOL, CurrencyAmount, Currency } from '@raydium-io/raydium-sdk';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey, ComputeBudgetProgram, VersionedTransaction, TransactionMessage } from '@solana/web3.js';
import { useRaydium } from '../../hooks/useRaydium';
import { useSplToken } from '../../hooks/useSplToken';
import { useState, useEffect } from 'react';

import '../../ManualSwap.css';
import { useBackend } from '../../hooks/useBackend';
import { useJito } from '../../hooks/useJito';
import { usePump } from '../../hooks/usePump';
import { getAssociatedBondingCurvePubkey, getBondingCurvePubkey } from '../../utils/pump-fun';
import {
    createAssociatedTokenAccountInstruction,
    getAccount,
    getAssociatedTokenAddressSync,
    TokenAccountNotFoundError,
    TokenInvalidAccountOwnerError,
} from '@solana/spl-token';
import { manualSwapJitoTip } from '../../../ConfigFees';

const BALANCE_PRECISION = 4;

const DEFAULT_BALANCE = new CurrencyAmount(new Currency(9), 0).toFixed(4);

const DEFAULT_TOKEN = '';
const DEFAULT_TOKEN_NAME = '';
const DEFAULT_TOKEN_SYMBOL = '';

const MIN_JITO_TIP = 0.0001;
const MIN_SOL_BUY_AMOUNT = 0;
const MIN_TOKEN_SELL_AMOUNT = 0;
const MIN_PRIORITY_FEE = 0;

const SOL_BUY_AMOUNTS = [0.01, 0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 5, 10];
const TOKEN_SELL_PERCENTAGES = [100, 75, 50, 20, 10, 5, 1];

enum Marketplace {
    Pump = 'Pump',
    Raydium = 'Raydium',
}

export function ManualSwap({
    connection,
    allWallets,
    allWalletRoles,
    logMessage,
}: {
    connection: Connection;
    allWallets: Keypair[];
    allWalletRoles: string[];
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const { getTipInstructions, sendBundle } = useJito();
    const { fetchPoolKeys } = useBackend();
    const { swap } = useRaydium();
    const { getWalletTokenAccountByMint, getNameAndSymbol } = useSplToken();
    const { getBuyInstruction, getSellInstruction } = usePump();

    const [wallet, setWallet] = useState<Keypair>();
    const [solBalance, setSolBalance] = useState(DEFAULT_BALANCE);
    const [token, setToken] = useState(DEFAULT_TOKEN);
    const [tokenName, setTokenName] = useState(DEFAULT_TOKEN_NAME);
    const [tokenSymbol, setTokenSymbol] = useState(DEFAULT_TOKEN_SYMBOL);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [tokenBalance, setTokenBalance] = useState(DEFAULT_BALANCE);
    const [jitoTip, setJitoTip] = useState(manualSwapJitoTip);
    const [priorityFee, setPriorityFee] = useState(MIN_PRIORITY_FEE);
    const [marketplace, setMarketplace] = useState<Marketplace>(Marketplace.Raydium);
    const [solBuyAmount, setSolBuyAmount] = useState(MIN_SOL_BUY_AMOUNT);
    const [tokenSellAmount, setTokenSellAmount] = useState(MIN_TOKEN_SELL_AMOUNT);

    function getWalletLabel(keypair: Keypair) {
        const base58Key = keypair.publicKey.toBase58();
        return `${base58Key.substring(0, 5)}...${base58Key.substring(base58Key.length - 5)}`;
    }

    const selectedWalletLabels = allWallets.map(getWalletLabel);

    const roleCounters: { [key: string]: number } = { MIX: 1, BUNDLEBUY: 1 };

    function getWalletRole(role: string) {
        const formattedRole = `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}`;

        if (roleCounters.hasOwnProperty(role)) {
            return `${formattedRole}${roleCounters[role]++}`;
        } else {
            return formattedRole;
        }
    }

    const selectedWalletRoles = allWalletRoles.map(getWalletRole);

    useEffect(() => setWallet(allWallets.length > 0 ? allWallets[0] : undefined), [allWallets]);

    const fetchTokenData = async (mint: string) => {
        setTokenName('');
        setTokenSymbol('');
        setIsTokenValid(false);
        setTokenBalance(DEFAULT_BALANCE);
        try {
            const [name, symbol] = (await getNameAndSymbol(connection, mint, logMessage))!;
            setTokenName(name);
            setTokenSymbol(symbol);
            setIsTokenValid(true);
        } catch (error) {
            logMessage('Invalid mint public key!', 'error');
        }
    };

    useEffect(() => {
        if (token !== '') fetchTokenData(token);
    }, [token]);

    const fetchSolBalance = async (wallet: PublicKey) => {
        const balance = await connection.getBalance(wallet);
        return (balance / LAMPORTS_PER_SOL).toFixed(BALANCE_PRECISION);
    };

    const fetchTokenBalance = async (mint: string, wallet: PublicKey) => {
        const tokenAccount = await getWalletTokenAccountByMint(connection, wallet, new PublicKey(mint));
        const tokenAccountBalance = await connection.getTokenAccountBalance(tokenAccount[0].pubkey, 'processed');
        return new CurrencyAmount(new Currency(tokenAccountBalance.value.decimals), tokenAccountBalance.value.amount).toFixed(BALANCE_PRECISION);
    };

    const fetchBalances = async (wallet: Keypair | undefined, mint: string) => {
        const [newSolBalance, newTokenBalance] = wallet
            ? await Promise.all([
                  fetchSolBalance(wallet.publicKey).catch(() => DEFAULT_BALANCE),
                  fetchTokenBalance(mint, wallet.publicKey).catch(() => DEFAULT_BALANCE),
              ])
            : [DEFAULT_BALANCE, DEFAULT_BALANCE];

        setSolBalance(newSolBalance);
        setTokenBalance(newTokenBalance);
    };

    useEffect(() => {
        fetchBalances(wallet, token);
        const id = setInterval(() => fetchBalances(wallet, token), 2000);

        return () => clearInterval(id);
    }, [wallet, token]);

    const handleBuy = {
        [Marketplace.Raydium]: async () => {
            logMessage(`Processing Raydium buy`);

            const lamportsBuyAmount = solBuyAmount * LAMPORTS_PER_SOL;

            const poolKeys = await fetchPoolKeys(token);

            const bundleId = await swap(
                connection,
                wallet!,
                wallet!,
                poolKeys,
                lamportsBuyAmount,
                1,
                new PublicKey(WSOL.mint),
                new PublicKey(token),
                priorityFee,
                jitoTip,
                logMessage,
            );

            logMessage(`Raydium buy bundle sent. Bundle id: ${bundleId}`, 'success');
        },
        [Marketplace.Pump]: async () => {
            logMessage(`Processing Pump buy`);

            const mintPubkey = new PublicKey(token);
            const ata = getAssociatedTokenAddressSync(mintPubkey, wallet!.publicKey);
            const bondingCurvePubkey = getBondingCurvePubkey(mintPubkey);
            const associatedBondingCurvePubkey = getAssociatedBondingCurvePubkey(mintPubkey, bondingCurvePubkey);

            const ixs = await Promise.all([
                getTipInstructions(wallet!, jitoTip),
                getBuyInstruction(connection, wallet!, mintPubkey, bondingCurvePubkey, associatedBondingCurvePubkey, ata, solBuyAmount, 100),
            ]);

            ixs.unshift(
                ComputeBudgetProgram.setComputeUnitPrice({
                    microLamports: priorityFee,
                }),
            );

            {
                try {
                    await getAccount(connection, ata, 'processed');
                } catch (error: unknown) {
                    if (error instanceof TokenAccountNotFoundError || error instanceof TokenInvalidAccountOwnerError) {
                        ixs.unshift(createAssociatedTokenAccountInstruction(wallet!.publicKey, ata, wallet!.publicKey, mintPubkey));
                    } else {
                        throw error;
                    }
                }
            }

            const latestBlockhash = await connection.getLatestBlockhash('processed');

            const tx = new VersionedTransaction(
                new TransactionMessage({
                    payerKey: wallet!.publicKey,
                    instructions: ixs,
                    recentBlockhash: latestBlockhash.blockhash,
                }).compileToV0Message(),
            );

            tx.sign([wallet!]);

            const bundleId = await sendBundle(connection, [tx], wallet!, jitoTip, true);

            logMessage(`Pump buy bundle sent. Bundle id: ${bundleId}`, 'success');
        },
    };

    const handleSell = {
        [Marketplace.Raydium]: async () => {
            logMessage(`Processing Raydium sell`);

            const poolKeys = await fetchPoolKeys(token);

            const tokenDecimals = poolKeys.baseMint.toBase58() == new PublicKey(token).toBase58() ? poolKeys.baseDecimals : poolKeys.quoteDecimals;

            const tokenSellAmountWithDecimals = tokenSellAmount * 10 ** tokenDecimals;

            const bundleId = await swap(
                connection,
                wallet!,
                wallet!,
                poolKeys,
                tokenSellAmountWithDecimals,
                1,
                new PublicKey(token),
                new PublicKey(WSOL.mint),
                priorityFee,
                jitoTip,
                logMessage,
            );

            logMessage(`Raydium sell bundle sent. Bundle id: ${bundleId}`, 'success');
        },
        [Marketplace.Pump]: async () => {
            logMessage(`Processing Pump sell`);

            const mintPubkey = new PublicKey(token);
            const ata = getAssociatedTokenAddressSync(mintPubkey, wallet!.publicKey);
            const bondingCurvePubkey = getBondingCurvePubkey(mintPubkey);
            const associatedBondingCurvePubkey = getAssociatedBondingCurvePubkey(mintPubkey, bondingCurvePubkey);

            const ixs = await Promise.all([
                getTipInstructions(wallet!, jitoTip),
                getSellInstruction(connection, wallet!, mintPubkey, bondingCurvePubkey, associatedBondingCurvePubkey, ata, tokenSellAmount, 100),
            ]);

            ixs.unshift(
                ComputeBudgetProgram.setComputeUnitPrice({
                    microLamports: priorityFee,
                }),
            );

            const latestBlockhash = await connection.getLatestBlockhash('processed');

            const tx = new VersionedTransaction(
                new TransactionMessage({
                    payerKey: wallet!.publicKey,
                    instructions: ixs,
                    recentBlockhash: latestBlockhash.blockhash,
                }).compileToV0Message(),
            );

            tx.sign([wallet!]);

            const bundleId = await sendBundle(connection, [tx], wallet!, jitoTip, true);

            logMessage(`Pump sell bundle sent. Bundle id: ${bundleId}`, 'success');
        },
    };

    return (
        <section className='manual-swap'>
            <section className='manual-swap-left'>
                <div className='token-info-wrap'>
                    <label className='swap-token-label'>
                        <b>Token</b>
                        <input type='text' value={token} onChange={(event) => setToken(event.target.value)} placeholder='Token Mint Address' />
                    </label>
                    <p>{isTokenValid ? <b className='token-valid'>Valid</b> : <b className='token-invalid'>Invalid</b>}</p>
                </div>
                <div className='wallet-info-wrap'>
                    <label className='swap-token-select'>
                        <b>Wallet</b>
                        <select
                            value={wallet ? allWallets.findIndex((_) => _.publicKey.toBase58() == wallet.publicKey.toBase58()) : undefined}
                            onChange={(event) => {
                                setSolBalance(DEFAULT_BALANCE);
                                setTokenBalance(DEFAULT_BALANCE);
                                setWallet(allWallets[Number(event.target.value)]);
                            }}
                        >
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                    </label>
                    <p>
                        <b>Balance: </b>
                        {solBalance} SOL
                    </p>
                </div>
                <div className='swap-token-options'>
                    <label>
                        <span>Jito Tip</span>
                        <input
                            type='number'
                            value={jitoTip}
                            onChange={(event) => {
                                let jitoTip = Number(event.target.value);

                                if (!jitoTip || jitoTip < MIN_JITO_TIP) jitoTip = MIN_JITO_TIP;

                                setJitoTip(jitoTip);
                            }}
                            placeholder='Enter Jito tip'
                        />
                    </label>
                    <label>
                        <span>Priority Fee</span>
                        <input
                            type='number'
                            value={priorityFee}
                            onChange={(event) => {
                                let priorityFee = Number(event.target.value);

                                if (!priorityFee || priorityFee < MIN_PRIORITY_FEE) priorityFee = MIN_PRIORITY_FEE;

                                setPriorityFee(priorityFee);
                            }}
                            placeholder='Enter Priority Fee'
                        />
                    </label>
                </div>
                <div className={'manual-swap-marketplace'}>
                    <h3 className={'manual-swap-marketplace-header'}>Marketplace</h3>
                    <button
                        className={'manual-swap-marketplace-button'}
                        onClick={() => setMarketplace((prevState) => (prevState == Marketplace.Raydium ? Marketplace.Pump : Marketplace.Raydium))}
                    >
                        {marketplace}
                    </button>
                </div>
                <h3 className='manual-swap-header'>Buy</h3>
                <div>
                    <div className='manual-buy-buttons-container'>
                        {SOL_BUY_AMOUNTS.map((solBuyAmount, index) => (
                            <button key={index} onClick={() => setSolBuyAmount(solBuyAmount)}>
                                {solBuyAmount}
                            </button>
                        ))}
                    </div>
                    <div className='custom-swap-container'>
                        <input
                            type='number'
                            placeholder='Custom'
                            value={solBuyAmount}
                            onChange={(event) => {
                                let solBuyAmount = Number(event.target.value);

                                if (!solBuyAmount || solBuyAmount < MIN_SOL_BUY_AMOUNT) solBuyAmount = MIN_SOL_BUY_AMOUNT;

                                setSolBuyAmount(solBuyAmount);
                            }}
                        />
                        <button disabled={!isTokenValid || !wallet} onClick={() => handleBuy[marketplace]()}>
                            BUY
                        </button>
                    </div>
                </div>
                <h3 className='manual-swap-header'>Sell</h3>
                <div>
                    <div className='manual-sell-buttons-container'>
                        {TOKEN_SELL_PERCENTAGES.map((tokenSellPercentage, index) => (
                            <button
                                key={index}
                                disabled={!isTokenValid || !wallet}
                                onClick={() => setTokenSellAmount((Number(tokenBalance) * tokenSellPercentage) / 100)}
                            >
                                {tokenSellPercentage}%
                            </button>
                        ))}
                    </div>
                    <div className='custom-swap-container'>
                        <input
                            type='number'
                            placeholder='Custom'
                            value={tokenSellAmount}
                            onChange={(event) => {
                                let tokenSellAmount = Number(event.target.value);

                                if (!tokenSellAmount || tokenSellAmount < MIN_TOKEN_SELL_AMOUNT) tokenSellAmount = MIN_TOKEN_SELL_AMOUNT;

                                setTokenSellAmount(tokenSellAmount);
                            }}
                        />
                        <button disabled={!isTokenValid || !wallet} onClick={() => handleSell[marketplace]()}>
                            SELL
                        </button>
                    </div>
                </div>
            </section>
            {isTokenValid && (
                <section className='manual-swap-right'>
                    <div className='token-name-symbol'>
                        <p>{tokenName}</p>
                        <p>{tokenSymbol}</p>
                    </div>
                    <div className='swap-token-balance'>
                        <b>Token Balance:</b>
                        <p>{tokenBalance}</p>
                    </div>
                    {/*<div>*/}
                    {/*    <b>Value:</b>*/}
                    {/*</div>*/}
                    {/*<div>*/}
                    {/*    <b>Token LP Pool:</b>*/}
                    {/*</div>*/}
                </section>
            )}
        </section>
    );
}
